import { call, put, all, select, takeLatest, takeEvery } from 'typed-redux-saga'
import {
	errorHandler,
	notifyMessageCreator,
	NOTIFICATION_TYPE,
	modalsSlice,
	fileApi,
	loadersSlice,
	gridSlice,
} from 'Modules'
import { RequestParams, ResponseCatalog, CertificateModel } from 'Models'
import { FormNames } from 'Constants/FormNames'
import { urls } from 'Constants/Urls'
import { getBase64File, readFile } from 'Helpers'
import { FrontUrls } from 'Constants/FrontUrls'
import { MODAL_NAME } from 'Constants/ModalName'
import { Deserialize } from 'cerialize'
import { createSignature } from 'crypto-pro'
import { routerActions } from 'connected-react-router'
import {
	RbidosAccessModel,
	rbidosAccessApi,
	rbidosAccessSlice,
	rbidosAccessSelector,
	StatusRbidosSlug,
	rbidosAccessNS,
} from 'Pages/registry_business_access_neo'

function* getAll(action: rbidosAccessNS.GetAll) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		const { params, clear, callback } = action.payload
		const { page, per_page, query, ...fields } = params
		const requestParams = new RequestParams(clear ? 1 : page, per_page, query, fields)
		if (clear) {
			yield* put(rbidosAccessSlice.actions.clearAll())
		}
		const res = yield* call(rbidosAccessApi.getAll, requestParams)
		const accesses = new ResponseCatalog<RbidosAccessModel>(res)
		if (!clear) {
			const oldEntries = yield* select(rbidosAccessSelector.getAll)
			accesses.entries = oldEntries.concat(res.entries)
		}
		yield* put(rbidosAccessSlice.actions.setRequestParams(requestParams))
		yield* put(rbidosAccessSlice.actions.setAll(accesses))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getCurrent(action: rbidosAccessNS.GetCurrent) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(rbidosAccessApi.getAll)
		yield* put(rbidosAccessSlice.actions.setCurrent(res.entries[0]))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* createOne(action: rbidosAccessNS.CreateOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, type, callback } = action.payload
		const res = yield* call(rbidosAccessApi.createOne, id, type)
		yield* put(rbidosAccessSlice.actions.setCurrent(res))
		callback?.(res)
	} catch (error) {
		if (
			error?.data.errors[0]?.partner_nalog_registration_date ||
			error?.data.errors[0]?.partner_registration_date
		) {
			yield* put(rbidosAccessSlice.actions.setErrorPartnerNalogRegDate(true))
		} else yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getOne(action: rbidosAccessNS.GetOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id } = action.payload
		const res = yield* call(rbidosAccessApi.getOne, id)
		yield* put(rbidosAccessSlice.actions.setCurrent(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* getNewAgent(action: rbidosAccessNS.GetNewAgent) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id } = action.payload
		const res = yield* call(rbidosAccessApi.getNewAgent, id)
		yield* put(rbidosAccessSlice.actions.setNewAgent(res))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* updateOne(action: rbidosAccessNS.UpdateOne) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, data, callback } = action.payload
		const res = yield* call(rbidosAccessApi.updateOne, id, data)
		yield* put(rbidosAccessSlice.actions.setCurrent(res))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error, FormNames.RBIDOS_ACCESS.EDIT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

export function* statusChange(action: rbidosAccessNS.StatusChange) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, status, message, callback } = action.payload
		yield* call(rbidosAccessApi.statusChange, id, status, message)
		callback?.()
		yield* put(routerActions.push(FrontUrls.cabinet_redesign.registry_business_access_neo_index))
	} catch (error) {
		yield* call(errorHandler, error, FormNames.RBIDOS_ACCESS.EDIT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* signDocument(data: {
	id: number
	documentId: number
	cert?: CertificateModel
	signatureFile?: File
}) {
	const { id, documentId, cert, signatureFile } = data
	let signature
	if (!signatureFile && cert) {
		const request = yield* select(rbidosAccessSelector.getCurrentRequest)
		const document = request?.documents?.find((doc) => doc.id === documentId)
		const file = yield* call(
			fileApi.getFile,
			urls.rbidos_access.attachment
				.replace(':request_id', String(id))
				.replace(':id', String(document.id))
		)
		const dataUrl = yield* call(readFile, file)
		const dataBase64 = getBase64File(dataUrl)
		signature = yield* call(createSignature, cert.thumbprint, dataBase64, false)
	}
	if (signatureFile) {
		if (signatureFile.name.includes('.pdf')) {
			signature = yield* call(readFile, signatureFile, true)
		} else {
			const dataUrl = yield* call(readFile, signatureFile)
			signature = getBase64File(dataUrl)
		}
	}
	yield* call(rbidosAccessApi.signDocument, id, documentId, signature, cert)
}

function* signDocuments(action: rbidosAccessNS.SignDocuments) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		// @ts-ignore
		const { id, documentsIds, signatures, cert } = action.payload
		if (documentsIds?.length && cert)
			yield* all(
				documentsIds.map((documentId) =>
					call(signDocument, { id, documentId, cert: Deserialize(cert, CertificateModel) })
				)
			)
		if (signatures) {
			yield* all(
				Object.keys(signatures).map((documentId) =>
					call(signDocument, {
						id,
						documentId: Number(documentId),
						signatureFile: signatures[documentId],
					})
				)
			)
		}
		const res = yield* call(rbidosAccessApi.getOne, id.toString())
		yield* put(rbidosAccessSlice.actions.setCurrent(res))
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.SIGN_RBIDOS_DOCS))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'rbidos.access_sign_documents.error', {
			options: { type: NOTIFICATION_TYPE.ERROR },
		})
	}
}

function* sendSignature(action: rbidosAccessNS.SendSignature) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		yield* call(rbidosAccessApi.sendSignature, action.payload)
		const request = yield* select(rbidosAccessSelector.getCurrentRequest)
		// TODO 5500 временное решение
		yield* put(
			rbidosAccessSlice.actions.setCurrent(
				Object.assign(new RbidosAccessModel(), request, {
					status: StatusRbidosSlug.VERIFICATION_OF_SIGNATURE,
				})
			)
		)
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'rbidos.access_send_signature.success')
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(notifyMessageCreator, 'rbidos.access_send_signature.error', {
			options: { type: NOTIFICATION_TYPE.ERROR },
		})
	}
}

function* synchronize(action: rbidosAccessNS.Synchronize) {
	try {
		yield* call(rbidosAccessApi.synchronize, action.payload)
		yield* call(notifyMessageCreator, 'rbidos.synchronize_partner_request.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* checkInfotecsStatus(action: rbidosAccessNS.CheckInfotecsStatus) {
	try {
		yield* call(rbidosAccessApi.checkInfotecsStatus, action.payload)
		yield* call(notifyMessageCreator, 'common.status_check_request.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* rbidosAccessSaga() {
	yield* takeLatest(rbidosAccessSlice.actions.getAll.type, getAll)
	yield* takeLatest(rbidosAccessSlice.actions.getOne.type, getOne)
	yield* takeLatest(rbidosAccessSlice.actions.createOne.type, createOne)
	yield* takeLatest(rbidosAccessSlice.actions.updateOne.type, updateOne)
	yield* takeLatest(rbidosAccessSlice.actions.getCurrent.type, getCurrent)
	yield* takeLatest(rbidosAccessSlice.actions.getNewAgent.type, getNewAgent)
	yield* takeLatest(rbidosAccessSlice.actions.statusChange.type, statusChange)
	yield* takeEvery(rbidosAccessSlice.actions.signDocuments, signDocuments)
	yield* takeEvery(rbidosAccessSlice.actions.sendSignature, sendSignature)
	yield* takeLatest(rbidosAccessSlice.actions.synchronize.type, synchronize)
	yield* takeLatest(rbidosAccessSlice.actions.checkInfotecsStatus.type, checkInfotecsStatus)
}
